import { useEffect, useRef, useState } from 'react';
import { cn } from '@/utils/cn';

interface OnboardingMascotVideoProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'onboarding';
  onReady?: () => void;
}

const OnboardingMascotVideo = ({ className, size = 'onboarding', onReady }: OnboardingMascotVideoProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const readyNotifiedRef = useRef(false);
  const [isReady, setIsReady] = useState(false);

  const notifyReady = () => {
    if (readyNotifiedRef.current) return;
    setIsReady(true);
  };

  // Notify only after React has committed the visible video element.
  useEffect(() => {
    if (!isReady || readyNotifiedRef.current) return;
    readyNotifiedRef.current = true;
    onReady?.();
  }, [isReady, onReady]);

  const sizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-12 w-12',
    lg: 'h-16 w-16',
    xl: 'h-24 w-24',
    xxl: 'h-32 w-32',
    onboarding: 'h-[86px] w-[86px]',
  };

  const sizeClass = sizeClasses[size];

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let mounted = true;
    const startPlayback = () => {
      void video.play()
        .then(() => {
          if (mounted) notifyReady();
        })
        .catch(() => {
          // The video is muted, but some WebViews may still reject autoplay.
          // Reveal the first decoded frame without exposing native controls.
          if (mounted) notifyReady();
        });
    };

    if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      startPlayback();
    } else {
      video.addEventListener('canplay', startPlayback, { once: true });
      video.load();
    }

    return () => {
      mounted = false;
      video.removeEventListener('canplay', startPlayback);
    };
  }, [onReady]);

  // Do not mount the video element before it can start. This prevents a
  // browser/WebView play overlay or an unready black frame from flashing.
  if (!isReady) {
    return (
      <video
        ref={videoRef}
        className="absolute h-px w-px opacity-0 pointer-events-none"
        src="/default_animation.mp4"
        autoPlay
        muted
        playsInline
        preload="auto"
        controls={false}
        disablePictureInPicture
        aria-hidden="true"
      />
    );
  }

  return (
    <div className={cn('flex items-center justify-center bg-white', sizeClass)}>
      <video
        ref={videoRef}
        className={cn('h-full w-full shrink-0 object-contain', className)}
        src="/default_animation.mp4"
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        controls={false}
        disablePictureInPicture
        aria-hidden="true"
      />
    </div>
  );
};

export default OnboardingMascotVideo;
